import React, { createRef, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { addOverlay, removeOverlay } from '../../Actions/Overlay.action';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Thumbs, FreeMode, Zoom, Keyboard } from 'swiper';
import 'swiper/css/bundle';
import 'swiper/css/zoom';
import Icon from '../Icon';
import { translate } from '../../Services/translation';
import { text } from '../../styling/mixins/typography';
import SliderImage from '../../Types/SliderImage';
import styled, { createGlobalStyle } from 'styled-components';
import { getColor } from '../../styling/mixins/colors';
import { showFor } from '../../styling/utils/visibility';

interface FullscreenImageSliderProps {
  showCloseButton?: boolean;
  images: SliderImage[];
  initialSlide?: number;
  onClosed?: () => void;
}

interface ZoomInfoProps {
  $zoomInfoVisible: boolean;
}

const StyledSwiper = styled(Swiper)`
  height: 100%;

  & .swiper-pagination {
    bottom: 3.2rem !important;
  }
`;

const StyledImageSliderWrapper = styled.div`
  z-index: 65;
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100vh;
  background-color: ${getColor('white')};

  .content {
    height: 100%;
  }

  img {
    height: 100%;
  }
`;

const StyledCloseButton = styled(Icon)`
  z-index: 55;
  font-size: 2rem;
`;

const StyledZoomInfo = styled.div<ZoomInfoProps>`
  ${showFor('large')}
  ${text('small')}
  display: flex;
  align-items: center;
  position: absolute;
  top: 1.6rem;
  left: 1.6rem;
  background-color: rgba(0, 0, 0, 0.5);
  color: ${getColor('white')};
  border-radius: 1.6rem;
  padding: 0.4rem 0.8rem;
  z-index: 60;
  pointer-events: ${(p) => (p.$zoomInfoVisible ? '1' : 'none')};
  opacity: ${(p) => (p.$zoomInfoVisible ? '1' : '0')};
  transition: opacity 0.125s ease-in-out;

  & svg {
    margin-right: 0.4rem;
  }
`;

const GlobalImageSliderStyling = createGlobalStyle`
body #overlay-container .overlay--FullscreenImageSlider {
  z-index: 50;
}
`;

const FullscreenImageSlider = ({
  showCloseButton = true,
  images,
  initialSlide = 0,
  onClosed,
}: FullscreenImageSliderProps) => {
  const dispatch = useDispatch();
  const contentRef = createRef<HTMLDivElement>();

  const [open, setOpen] = useState(true);
  const [isZoomed, setIsZoomed] = useState<boolean>(false);
  const [zoomInfoVisible, setZoomInfoVisible] = useState<boolean>(true);
  const [hasLoadedOriginalImage, setHasLoadedOriginalImage] = useState<
    number[]
  >([]);
  const imageCount = useMemo(() => images.length, [images]);

  const onKeyDown = (e) => {
    if (e.key == 'Escape') {
      setOpen(false);
    }
  };

  useEffect(() => {
    dispatch(
      addOverlay('FullscreenImageSlider', () => {
        setOpen(false);
      })
    );
    document.addEventListener('keydown', onKeyDown, false);

    return () => {
      document.removeEventListener('keydown', onKeyDown, false);
    };
  }, [dispatch]);

  useEffect(() => {
    if (!open) {
      dispatch(removeOverlay('FullscreenImageSlider'));
      onClosed && onClosed();
    }
  }, [dispatch, open, onClosed]);

  useEffect(() => {
    setTimeout(() => setZoomInfoVisible(false), 5000);
  }, []);

  const onZoom = (slideIndex: number, scale: number) => {
    setIsZoomed(scale > 1);
    if (!hasLoadedOriginalImage.includes(slideIndex)) {
      setHasLoadedOriginalImage((hasLoadedOriginalImage) => [
        ...hasLoadedOriginalImage,
        slideIndex,
      ]);
    }
  };

  return open ? (
    <StyledImageSliderWrapper>
      <StyledZoomInfo
        onMouseOut={() => setZoomInfoVisible(false)}
        $zoomInfoVisible={zoomInfoVisible}
      >
        <Icon name="info" />
        {translate('fullscreen-image-slider.double-click-to-zoom')}
      </StyledZoomInfo>
      <div className="content" ref={contentRef}>
        {showCloseButton && (
          <div className="absolute top-5 right-5 cursor-pointer z-50 p-2 bg-white rounded-full border border-primary-500">
            <StyledCloseButton
              name="close"
              onClick={() => setOpen(false)}
              className="w-10 h-10"
            />
          </div>
        )}

        <StyledSwiper
          loop={true}
          slidesPerView={1}
          initialSlide={initialSlide}
          keyboard={true}
          zoom={{
            toggle: true,
          }}
          modules={[Pagination, Thumbs, FreeMode, Zoom, Keyboard]}
          onZoomChange={(swiper, scale) => onZoom(swiper.realIndex, scale)}
          pagination={{
            dynamicBullets: imageCount > 8 ? true : false,
            dynamicMainBullets: 3,
          }}
        >
          {images.map((img, idx) => (
            <SwiperSlide
              key={'fullwidth-image-slide' + idx + img.hdSrc}
              zoom={true}
            >
              <img
                src={
                  isZoomed || hasLoadedOriginalImage.includes(idx)
                    ? img.originalSrc
                    : img.hdSrc
                }
                loading={idx == 0 ? 'eager' : 'lazy'}
              />
            </SwiperSlide>
          ))}
        </StyledSwiper>
        {/* {showThumbnails && (
            <SliderThumbnails
              images={images}
              sliderRef={sliderRef}
              activeSlide={currentSlide}
            />
          )} */}
      </div>
      <GlobalImageSliderStyling />
    </StyledImageSliderWrapper>
  ) : null;
};

export default FullscreenImageSlider;
